import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getChartStartDate } from 'helpers/dates';
import { comparePropsIgnoringFunctions } from 'helpers/memo';
import ErrorBoundary from 'components/shared/ErrorBoundary';
import ControlledTabs from 'components/shared/ControlledTabs';
import HollowSelect from 'components/trade/HollowSelect';
import Row from 'components/shared/Row';
import Card from 'components/trade/Card';
import CandlestickChart from './CandlestickChart';
import TradingViewChart from './TradingViewChart';
import DepthChart from './DepthChart';
import styles from './styles.module.scss';

const frequencyTabs = [
  { label: '1m' },
  { label: '5m' },
  { label: '15m' },
  { label: '1h' },
  { label: '6h' },
  { label: '1d' }
];
const frequencyOptions = { 0: '1m', 1: '5m', 2: '15m', 3: '1h', 4: '6h', 5: '1d' };
const FREQ_VALUES = ['Min', '5Min', '15Min', 'H', '6H', 'D'];

function TradeChart(props) {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState(0);
  const [frequency, setFrequency] = useState(4);
  const frequencyValue = FREQ_VALUES[frequency];
  const [start, setStart] = useState(getChartStartDate(frequencyValue));
  const chartTabs = [
    { label: t('market_trading_view') },
    { label: t('market_price_chart') },
    { label: t('market_depth_chart') }
  ];
  const chartOptions = { 1: t('market_price_chart'), 2: t('market_depth_chart') };
  useEffect(() => {
    if (props.isMobile && chartType === 0) setChartType(1);
  }, [props.isMobile, chartType]);

  const handleFrequencyClick = freq => {
    setFrequency(freq);
    setStart(getChartStartDate(FREQ_VALUES[freq]));
  };

  const refetchOnError = () => {
    if (props.markets.error) props.markets.refetch();
    if (props.trades.error) props.trades.refetch();
  };

  const { loading } = props.markets;
  const error = props.markets.error || props.trades.error;

  if (!props.isMobile) {
    return (
      <Card
        cardStyles={[styles.card]}
        contentWrapperStyles={[styles.cardContent]}
        error={error}
        loading={loading}
        errorRefetch={refetchOnError}
        headerComponent={
          <div className={styles.tabWrap}>
            <ControlledTabs
              tabs={chartTabs}
              activeTabIndex={chartType}
              setActiveTabIndex={setChartType}
              rowStyles={[styles.textTabRow]}
              buttonStyles={[styles.textTabButton]}
              border={false}
              trading
            />
            {chartType === 1 && (
              <div className={styles.xlControls}>
                <ControlledTabs
                  tabs={frequencyTabs}
                  activeTabIndex={frequency}
                  setActiveTabIndex={handleFrequencyClick}
                  rowStyles={[styles.textTabRow]}
                  buttonStyles={[styles.textTabButton]}
                  border={false}
                  trading
                />
              </div>
            )}
          </div>
        }>
        <ErrorBoundary>
          {chartType === 0 && (
            <TradingViewChart
              pair={props.pair}
              market={props.markets.data.markets}
              refreshRate={props.refreshRate}
              key={props.pair} // forces re-mount when pair changes
            />
          )}
          {chartType === 1 && (
            <div className={styles.candlestickContainer}>
              {/* The key below forces the chart to re-render when pair or frequency changes. Not doing so was causing issues with the chart disappearing */}
              <CandlestickChart
                pair={props.pair}
                refreshRate={props.refreshRate}
                frequency={frequencyValue}
                start={start}
                market={props.markets.data.markets}
                key={`${props.pair}-${frequencyValue}`}
              />
              <div className={styles.lgControls}>
                <ControlledTabs
                  tabs={frequencyTabs}
                  activeTabIndex={frequency}
                  setActiveTabIndex={handleFrequencyClick}
                  rowStyles={[styles.textTabRow]}
                  buttonStyles={[styles.textTabButton]}
                  border={false}
                  trading
                />
              </div>
            </div>
          )}
          {chartType === 2 && (
            <DepthChart
              pair={props.pair}
              market={props.markets.data.markets}
              orderBook={props.orderBook.data}
              marketPrice={props.trades.data.marketPrice}
              refreshRate={props.refreshRate}
            />
          )}
        </ErrorBoundary>
      </Card>
    );
  }
  return (
    <Card error={error} loading={loading} errorRefetch={refetchOnError} isMobile={props.isMobile}>
      <ErrorBoundary>
        <Row styles={[styles.mobileChartControls]}>
          <HollowSelect
            id="frequency"
            name={t('market_chart_freq')}
            options={chartOptions}
            value={chartType}
            onChange={e => setChartType(parseInt(e.target.value, 10))}
            wrapperStyles={[styles.hollowTypeControl]}
          />
          {chartType === 1 && (
            <HollowSelect
              id="frequency"
              name={t('market_chart_freq')}
              options={frequencyOptions}
              value={frequency}
              onChange={e => handleFrequencyClick(parseInt(e.target.value, 10))}
            />
          )}
        </Row>
        {chartType === 1 && (
          <div className={styles.candlestickContainer}>
            {/* The key below forces the chart to re-render when pair or frequency changes. Not doing so was causing issues with the chart disappearing */}
            <CandlestickChart
              pair={props.pair}
              refreshRate={props.refreshRate}
              frequency={frequencyValue}
              start={start}
              market={props.markets.data.markets}
              key={`${props.pair}-${frequencyValue}`}
              isMobile
            />
          </div>
        )}
        {chartType === 2 && (
          <DepthChart
            pair={props.pair}
            market={props.markets.data.markets}
            orderBook={props.orderBook.data}
            marketPrice={props.trades.data.marketPrice}
            refreshRate={props.refreshRate}
            isMobile
          />
        )}
      </ErrorBoundary>
    </Card>
  );
}

TradeChart.propTypes = {
  pair: PropTypes.string.isRequired,
  markets: PropTypes.object.isRequired,
  orderBook: PropTypes.object.isRequired,
  trades: PropTypes.object.isRequired,
  refreshRate: PropTypes.number.isRequired,
  isMobile: PropTypes.bool
};

TradeChart.defaultProps = {
  isMobile: false
};

export default memo(TradeChart, comparePropsIgnoringFunctions);
